'use client';

import type { MouseEvent } from 'react';

import { useRouter } from 'next/navigation';

import { hasInAppHistory } from '../navigation/navigationHistory';

export interface BackControls {
  /**
   * Click handler for an `<a>` / `<Link>` / `<button>`. On a primary click it
   * pops the history stack when there's an in-app entry to return to; otherwise
   * it REPLACES with `fallbackHref` if one was given (climbing to a parent
   * without growing the stack), else does nothing — letting a host `<a>` /
   * `<Link>`'s own `href` navigate. Modifier / non-primary clicks always fall
   * through so the browser can still open an href (e.g. new tab).
   */
  onClick: (event: MouseEvent<HTMLElement>) => void;
  /**
   * Imperative back for non-click callers (e.g. a Dialog `onOpenChange`): pop
   * history when possible, else REPLACE with `fallbackHref` (a no-op without one).
   */
  goBack: () => void;
}

/**
 * Base primitive for the app's "back" behavior: return to the previous page
 * when there is one, else fall back to a known destination. Prefer this over a
 * raw `router.push(target)` for back affordances — a forward push GROWS the
 * history stack (Back then re-enters the page you just left) instead of popping
 * it.
 *
 * This is the low-level layer; most code should reach for `BackLink` (simple
 * cases) or `BackHandler` (custom markup) rather than calling the hook directly.
 */
export function useBack(fallbackHref?: string): BackControls {
  const router = useRouter();

  const goBack = () => {
    if (hasInAppHistory()) {
      router.back();
    } else if (fallbackHref != null) {
      router.replace(fallbackHref);
    }
  };

  const onClick = (event: MouseEvent<HTMLElement>) => {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    if (hasInAppHistory()) {
      event.preventDefault();
      router.back();
    } else if (fallbackHref != null) {
      event.preventDefault();
      router.replace(fallbackHref);
    }
    // else: no in-app back and no fallback → let a host <a>/<Link>'s href navigate.
  };

  return { onClick, goBack };
}
